import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Grid,
  Heading,
  Input,
  Text,
  VStack,
  Icon,
  useColorMode,
  Image,
  Badge,
  HStack,
  Tag,
  useBreakpointValue,
  useColorModeValue,
  Code,
  Link,
  Stack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  useToast,
  Wrap,
  WrapItem,
  IconButton,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  FiMessageSquare,
  FiZap,
  FiVideo,
  FiClock,
  FiUsers,
  FiTool,
  FiShoppingCart,
  FiPackage,
  FiTrendingUp,
  FiGift,
  FiBarChart,
  FiSmartphone,
  FiCode,
  FiDatabase,
  FiGlobe,
  FiSettings,
  FiLayers,
  FiCheck,
  FiCrosshair,
  FiDelete,
  FiX,
} from "react-icons/fi";
import {
  RiRobotLine,
  RiCustomerService2Line,
  RiHammerFill,
} from "react-icons/ri";
import { keyframes } from "@emotion/react";
import { TypeAnimation } from "react-type-animation";
import { PublicNavbar } from "./layout/PublicNavbar";
import { FaDiceOne, FaDiceThree, FaDiceTwo, FaYoutube } from "react-icons/fa";
import { Footer } from "./layout/Footer";
import { Carousel } from "react-responsive-carousel";

const gradientAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const glowAnimation = keyframes`
  0% {
    opacity: 0.5;
    transform: translate(-50%, -50%) scale(0.8);
  }
  50% {
    opacity: 0.8;
    transform: translate(-50%, -50%) scale(1.2);
  }
  100% {
    opacity: 0.5;
    transform: translate(-50%, -50%) scale(0.8);
  }
`;

const FeatureCard: React.FC<{
  icon: React.ElementType;
  title: string;
  description: string;
}> = ({ icon, title, description }) => {
  const { colorMode } = useColorMode();

  return (
    <Box
      p={6}
      borderRadius="xl"
      border="1px"
      borderColor={colorMode === "dark" ? "gray.700" : "gray.200"}
      _hover={{
        transform: "translateY(-5px)",
        boxShadow: "xl",
      }}
      transition="all 0.3s">
      <VStack align="start" spacing={4}>
        <HStack>
          <Icon
            as={icon}
            w={6}
            h={6}
            color={colorMode === "dark" ? "brand.primary" : "brand.secondary"}
          />
          <Heading size="md" fontFamily="Nasalization">
            {title}
          </Heading>
        </HStack>
        <Text color={colorMode === "dark" ? "gray.400" : "gray.600"}>
          {description}
        </Text>
      </VStack>
    </Box>
  );
};

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3013";
const galleryImages = [
  "https://static.helpjuice.com/helpjuice_production/uploads/upload/image/7634/3148148/Logo_Nuvemshop__1_.png",
  "https://logowik.com/content/uploads/images/woocommerce6709.jpg",
  "ttps://logowik.com/content/uploads/images/803_shopify.jpg",
  "https://catalogo.ecommercebrasil.com.br/uploads/ecb-catalogo/tray-novamarca.png",
  "https://logospng.org/wp-content/uploads/mercado-shops.png",
];
export const Chatbot: React.FC = () => {
  const { colorMode } = useColorMode();
  const { t } = useTranslation();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [imageGallery, setImageGallery] = useState<string[]>(galleryImages);
  const [website, setWebsite] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const mainFeatures = [
    {
      icon: RiRobotLine,
      title: t("chatbot.features.agents.title", "Orquestração de Agentes"),
      description: t(
        "chatbot.features.agents.description",
        "Agentes de IA podem se comunicar e coordenar ações com outros agentes de IA especialistas em determinados setores do seu negócio."
      ),
    },
    {
      icon: RiHammerFill,
      title: t("chatbot.features.embed.title", "Uso de Ferramentas"),
      description: t(
        "chatbot.features.embed.description",
        "Agentes podem utilizar diversas ferramentas para diversas finalidades, buscar na web, preencher um formulário, comunicar com APIs, ERPs, CRMs."
      ),
    },
    {
      icon: FiPackage,
      title: t(
        "chatbot.agent.features.automation.title",
        "Automação Inteligente"
      ),
      description: t(
        "chatbot.agent.features.automation.description",
        "Automatize tarefas complexas com agentes que podem realizar sequências elaboradas de ações e entregar resultados completos e contextualizados."
      ),
    },
    {
      icon: FiMessageSquare,
      title: t(
        "chatbot.agent.features.channels.title",
        "Múltiplos Canais de Atendimento"
      ),
      description: t(
        "chatbot.agent.features.channels.description",
        "Disponibilize o mesmo agente no seu site, WhatsApp e outros canais de comunicação para uma experiência consistente."
      ),
    },
    {
      icon: FiDatabase,
      title: t("chatbot.features.knowledge.title", "Base de Conhecimento"),
      description: t(
        "chatbot.features.knowledge.description",
        "Alimente seus agentes com informações específicas sobre sua empresa, produtos, políticas e FAQs para respostas precisas e contextualizadas."
      ),
    },
    {
      icon: FiGlobe,
      title: t("chatbot.features.models.title", "Múltiplos Modelos de IA"),
      description: t(
        "chatbot.features.models.description",
        "Escolha entre os modelos mais populares do mercado, como ChatGPT, Claude e Gemini, selecionando a opção que melhor se adapta às suas necessidades."
      ),
    },
  ];

  const agentFeatures = [
    // {
    //   icon: FiPackage,
    //   title: t(
    //     "chatbot.agent.features.automation.title",
    //     "Automação Inteligente"
    //   ),
    //   description: t(
    //     "chatbot.agent.features.automation.description",
    //     "Automatize tarefas complexas com agentes que podem realizar sequências elaboradas de ações e entregar resultados completos e contextualizados."
    //   ),
    // },
    // {
    //   icon: FiMessageSquare,
    //   title: t(
    //     "chatbot.agent.features.channels.title",
    //     "Múltiplos Canais de Atendimento"
    //   ),
    //   description: t(
    //     "chatbot.agent.features.channels.description",
    //     "Disponibilize o mesmo agente no seu site, WhatsApp e outros canais de comunicação para uma experiência consistente."
    //   ),
    // },
    {
      icon: FiUsers,
      title: t(
        "chatbot.agent.features.personality.title",
        "Personalidade Ajustável"
      ),
      description: t(
        "chatbot.agent.features.personality.description",
        "Configure o tom de comunicação do seu agente para refletir a identidade da sua marca e criar uma experiência personalizada."
      ),
    },
    {
      icon: FiBarChart,
      title: t(
        "chatbot.agent.features.analytics.title",
        "Análise de Performance"
      ),
      description: t(
        "chatbot.agent.features.analytics.description",
        "Acompanhe métricas de atendimento e satisfação dos clientes para otimizar continuamente seus agentes."
      ),
    },
  ];

  const embedFeatures = [
    {
      icon: FiCode,
      title: t("chatbot.embed.features.easy.title", "Implementação Simples"),
      description: t(
        "chatbot.embed.features.easy.description",
        "Integre seu agente em qualquer site com apenas algumas linhas de código, sem necessidade de conhecimentos técnicos avançados."
      ),
    },
    {
      icon: FiSettings,
      title: t(
        "chatbot.embed.features.customization.title",
        "Personalização Total"
      ),
      description: t(
        "chatbot.embed.features.customization.description",
        "Adapte a aparência e comportamento do chat para combinar com a identidade visual do seu site."
      ),
    },
    {
      icon: FiLayers,
      title: t(
        "chatbot.embed.features.platforms.title",
        "Compatível com Todas as Plataformas"
      ),
      description: t(
        "chatbot.embed.features.platforms.description",
        "Funciona em qualquer site, e-commerce ou plataforma, independentemente da tecnologia utilizada."
      ),
    },
    {
      icon: FiTrendingUp,
      title: t("chatbot.embed.features.scalability.title", "Escalabilidade"),
      description: t(
        "chatbot.embed.features.scalability.description",
        "Suporte a milhares de conversas simultâneas sem comprometer o desempenho ou a qualidade do atendimento."
      ),
    },
  ];

  const defaultItems = [
    { title: "Qualificando Leads" },
    { title: "Conduzindo suas Vendas" },
    { title: "Inteligência Artificial Poderosa" },
    { title: "A partir de R$ 60/mês" },
    { title: "24/7 Sem descanço" },
    { title: "IA que entende o seu negócio" },
    { title: "Transformando seus visitantes em vendas" },
  ];

  const randomizedItems = [...defaultItems].sort(() => Math.random() - 0.5);

  let items;
  try {
    const translatedItems = t("chatbot.typeAnimationFeatures", {
      returnObjects: true,
    });
    items = Array.isArray(translatedItems) ? translatedItems : randomizedItems;
  } catch (error) {
    items = randomizedItems;
  }

  const sequence = items.flatMap((item): (string | number)[] => [
    item.title || "",
    3000,
  ]);

  const handlePlanSelect = (plan: string) => {
    setSelectedPlan(plan);
    onOpen();
  };
  const pricingRef = useRef<HTMLDivElement | null>(null);

  const scrollToPricing = () => {
    if (pricingRef && pricingRef.current) {
      window.scrollTo({
        top: pricingRef.current.offsetTop,
        behavior: "smooth",
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    if (!email || !name || !phone) {
      toast({
        title: t("common.contact.form.error"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setIsSubmitting(false);
      return;
    }

    try {
      await axios.post(`${API_URL}/contact`, {
        name,
        email,
        // phone,
        // plan: selectedPlan, // Include selected plan in the submission
        message: `Plano: ${selectedPlan};\n Telefone: ${phone}\n Website: ${website}`, // Add a default message or customize as needed
      });
      toast({
        // title: t("common.contact.form.success"),
        title:
          "Sucesso! Registramos seu interesse, nossa equipe de on-boarding entrará em contato em breve!",
        status: "success",
        duration: 8000,
        isClosable: true,
      });
      setName("");
      setEmail("");
      setPhone("");
      setSelectedPlan(null);
      onClose(); // Close modal on success
    } catch (error) {
      toast({
        title: t("common.contact.form.error"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      console.error("Submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    setImageGallery(imageGallery.sort(() => Math.random() - 0.5));
  }, []);
  return (
    <Box>
      {/* <PublicNavbar /> */}

      {/* Hero Section */}
      <Box
        position="relative"
        overflow="hidden"
        py={40}
        borderBottom="1px"
        borderColor={colorMode === "dark" ? "gray.700" : "gray.200"}>
        {/* Animated background */}
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bgGradient={
            colorMode === "dark"
              ? "linear(to-r, gray.900, brand.secondary, gray.900)"
              : "linear(to-r, white, brand.primary, white)"
          }
          backgroundSize="400% 400%"
          animation={`${gradientAnimation} 15s ease infinite`}
          opacity="0.1"
        />

        {/* Glowing orbs */}
        <Box
          position="absolute"
          top="90%"
          left="50%"
          width="800px"
          height="800px"
          borderRadius="full"
          bg={colorMode === "dark" ? "brand.secondary" : "white"}
          filter="blur(80px)"
          opacity="0.15"
          animation={`${glowAnimation} 7s ease-in-out infinite`}
          transform="translate(-50%, -50%)"
        />

        {/* Content */}
        <Container maxW="container.lg" position="relative">
          <VStack spacing={12} textAlign="center">
            {/* <HStack> */}

            <VStack spacing={8}>
              <Heading
                size="2xl"
                fontFamily="Nasalization"
                bgGradient="linear(to-r, brand.primary, brand.secondary)"
                bgClip="text">
                {t("chatbot.hero.title", "Agentes de Atendimento Araria")}
              </Heading>
              <Box maxW="2xl" minH="60px">
                <TypeAnimation
                  sequence={sequence}
                  wrapper="span"
                  speed={89}
                  style={{
                    fontSize: "1.25rem",
                    display: "inline-block",
                    fontFamily: "Nasalization",
                    color: useColorModeValue("#00392b", "#a0aec0"),
                  }}
                  repeat={Infinity}
                />
              </Box>

              <Text
                fontSize="xl"
                color={colorMode === "dark" ? "gray.400" : "gray.600"}
                maxW="2xl">
                {t(
                  "chatbot.hero.subtitle",
                  "Tenha uma IA que entende o seu negócio, atendendo seus clientes 24/7"
                )}
              </Text>
              <HStack spacing={4}>
                <Button
                  size="lg"
                  bg="brand.primary"
                  color="black"
                  _hover={{
                    bg: "brand.secondary",
                    color: "white",
                  }}
                  onClick={() => {
                    // handlePlanSelect("Plano Básico")
                    scrollToPricing();
                  }}
                  fontFamily="Nasalization">
                  {t("chatbot.hero.cta.primary", "Ver Planos!")}
                </Button>
                {/* <Button
                  size="lg"
                  variant="outline"
                  borderColor="brand.primary"
                  color={
                    colorMode === "dark" ? "brand.primary" : "brand.secondary"
                  }
                  _hover={{
                    bg: "brand.primary",
                    color: "white",
                  }}
                  onClick={() => {
                    window.open(
                      "https://blog.araria.com.br/novas-funcionalidades-personagens-agentes-e-embed-de-agentes/",
                      "_blank"
                    );
                  }}
                  fontFamily="Nasalization">
                  {t("chatbot.hero.cta.secondary", "Saiba Mais")}
                </Button> */}
                <IconButton
                  rounded="full"
                  variant="outline"
                  icon={<FaYoutube size="25px" />}
                  aria-label="YouTube"
                  width={50}
                  height={50}
                  ml={2}
                  onClick={() => {
                    window.open(
                      "https://www.youtube.com/watch?v=_a0PNYsmDN0",
                      "_blank"
                    );
                  }}
                />
              </HStack>
            </VStack>
            {/* <VStack>
            <iframe width="560" height="315" src="https://www.youtube.com/embed/_a0PNYsmDN0?si=AwHd2q8wkoo1IeIn" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>
            </VStack>
            </HStack> */}
          </VStack>
        </Container>
      </Box>

      {/* Images Section */}
      <Box py={20} bg={colorMode === "dark" ? "gray.900" : "white"}>
        <Container maxW="full">
          <VStack spacing={16}>
            <VStack spacing={4} textAlign="center">
              <Heading
                size="xl"
                fontFamily="Nasalization"
                color={
                  colorMode === "dark" ? "brand.primary" : "brand.secondary"
                }>
                {t("chatbot.stores.title", "Quem usa Araria?")}
              </Heading>
              <Text
                fontSize="lg"
                color={colorMode === "dark" ? "gray.400" : "gray.600"}
                maxW="2xl">
                {t(
                  "chatbot.stores.subtitle",
                  "As melhores plataformas estão usando Araria!"
                )}
              </Text>
            </VStack>

            <Box w="full" position="relative">
              <Container maxW="container.lg">
                <Grid
                  templateColumns={{
                    base: "repeat(2, 1fr)",
                    md: "repeat(4, 1fr)",
                    lg: "repeat(4, 1fr)",
                  }}
                  gap={4}
                  w="full">
                  {[
                    {
                      name: "nuvemshop",
                      invert: true,
                    },
                    {
                      name: "wordpress",
                      invert: false,
                    },
                    {
                      name: "tray",
                      invert: true,
                    },

                    {
                      name: "mercadoshops",
                      invert: false,
                    },
                    {
                      name: "vtex",
                      invert: false,
                    },
                    {
                      name: "shopify",
                      invert: false,
                    },
                    {
                      name: "wix",
                      invert: false,
                    },
                    {
                      name: "magento",
                      invert: false,
                    },
                  ].map((provider) => (
                    <VStack
                      key={provider.name}
                      p={4}
                      borderRadius="lg"
                      bg={colorMode === "dark" ? "gray.900" : "white"}
                      borderWidth="1px"
                      borderColor={
                        colorMode === "dark" ? "gray.700" : "gray.200"
                      }
                      spacing={2}
                      _hover={{
                        transform: "translateY(-5px)",
                        boxShadow: "xl",
                      }}
                      transition="all 0.3s"
                      position="relative"
                      display="flex"
                      flexDirection="column"
                      justifyContent="center"
                      alignItems="center">
                      <Image
                        src={`./assets/img/${provider.name}.png`}
                        alt={t(`chatbot.providers.${provider.name}`)}
                        height="50px"
                        filter={
                          colorMode === "dark"
                            ? provider.invert
                              ? "brightness(0) invert(1)"
                              : "brightness(1) invert(0)"
                            : undefined
                        }
                      />
                      <Text fontWeight="bold">
                        {t(`chatbot.providers.${provider.name}`)}
                      </Text>
                    </VStack>
                  ))}
                </Grid>
              </Container>
              {/* <Carousel
                showThumbs={false}
                showStatus={false}
                // infiniteLoop
                autoPlay
                interval={3000}
                swipeable
                emulateTouch
                centerMode
                centerSlidePercentage={isMobile ? 80 : 15}
                selectedItem={0}
                showArrows={false}
                showIndicators={isMobile ? false : true}
              >
                {imageGallery.map((imageId, index) => (
                  <Box key={index} cursor="pointer">
                    <Image
                      src={imageId}
                      alt={`Gallery Image ${imageId}`}
                      objectFit="cover"
                      loading="lazy"
                      maxW={isMobile ? "200px" : "200px"}
                      // h={isMobile ? "250px" : "auto"}
                      minH={isMobile ? "40px" : "40px"}
                      borderRadius="xl"
                      fallback={
                        <Box
                          width={isMobile ? "100%" : "300px"}
                          height={isMobile ? "200px" : "300px"}
                          bg="gray.200"
                          borderRadius="xl"
                        />
                      }
                    />
                  </Box>
                ))}
              </Carousel> */}
            </Box>
          </VStack>
        </Container>
      </Box>

      {/* Main Features Section */}
      <Box py={20} bg={colorMode === "dark" ? "gray.800" : "gray.100"}>
        <Container maxW="container.lg">
          <VStack spacing={16}>
            <VStack spacing={4} textAlign="center">
              <Heading
                size="xl"
                fontFamily="Nasalization"
                color={
                  colorMode === "dark" ? "brand.primary" : "brand.secondary"
                }>
                {t("chatbot.mainFeatures.title", "Agentes ≠ Chatbot")}
              </Heading>
              <Text
                fontSize="lg"
                color={colorMode === "dark" ? "gray.400" : "gray.600"}
                maxW="2xl">
                {t(
                  "chatbot.mainFeatures.subtitle",
                  "A arquitetura de Agentes de IA não é igual a de um Chatbot comum!"
                )}
              </Text>
            </VStack>

            <Grid
              templateColumns={{
                base: "1fr",
                md: "repeat(3, 1fr)",
                lg: "repeat(3, 1fr)",
              }}
              gap={8}
              w="full">
              {mainFeatures.map((feature, index) => (
                <FeatureCard key={index} {...feature} />
              ))}
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* Knowledge Base Section */}
      <Box py={20} bg={colorMode === "dark" ? "gray.900" : "white"}>
        <Container maxW="container.lg">
          <VStack spacing={16}>
            <VStack spacing={4} textAlign="center">
              <Heading
                size="xl"
                fontFamily="Nasalization"
                color={
                  colorMode === "dark" ? "brand.primary" : "brand.secondary"
                }>
                {t(
                  "chatbot.knowledge.title",
                  "Comece a usar em apenas 3 passos!"
                )}
              </Heading>
              <Text
                fontSize="lg"
                color={colorMode === "dark" ? "gray.400" : "gray.600"}
                maxW="2xl">
                {t(
                  "chatbot.knowledge.subtitle",
                  "Nossa implementação é simples e poderosa, em apenas 3 passos você já pode começar a usar nosso agente de IA para atender seus clientes!"
                )}
              </Text>
            </VStack>

            <Grid
              templateColumns={{
                base: "1fr",
                md: "repeat(2, 1fr)",
                lg: "repeat(3, 1fr)",
              }}
              gap={8}
              w="full">
              {[
                {
                  icon: FaDiceOne,
                  title: t(
                    "chatbot.knowledge.features.upload.title",
                    "Escolha seu plano"
                  ),
                  description: t(
                    "chatbot.knowledge.features.upload.description",
                    "Escolha o plano que melhor atende suas necessidades"
                  ),
                },
                {
                  icon: FaDiceTwo,
                  title: t(
                    "chatbot.knowledge.features.training.title",
                    "Configure seu Agente"
                  ),
                  description: t(
                    "chatbot.knowledge.features.training.description",
                    "Insira uma base de conhecimento para o seu Agente e/ou especialistas"
                  ),
                },
                {
                  icon: FaDiceThree,
                  title: t(
                    "chatbot.knowledge.features.performance.title",
                    "Insira o embed no seu site"
                  ),
                  description: t(
                    "chatbot.knowledge.features.performance.description",
                    "Copie o código de integração embed e cole em seu site."
                  ),
                },
              ].map((feature, index) => (
                <FeatureCard key={index} {...feature} />
              ))}
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Box
        py={20}
        bg={colorMode === "dark" ? "gray.800" : "gray.100"}
        ref={pricingRef}>
        <Container maxW="container.lg">
          <VStack spacing={16}>
            <VStack spacing={4} textAlign="center">
              <Heading
                size="xl"
                fontFamily="Nasalization"
                color={
                  colorMode === "dark" ? "brand.primary" : "brand.secondary"
                }>
                {t("chatbot.pricing.title", "Planos para cada Negócio")}
              </Heading>
              <Text
                fontSize="lg"
                color={colorMode === "dark" ? "gray.400" : "gray.600"}
                maxW="2xl">
                {t(
                  "chatbot.pricing.subtitle",
                  "Comece a transformar seu atendimento ao cliente num preço imbatível!"
                )}
              </Text>
            </VStack>

            <Stack
              direction={["column", "column", "row"]}
              spacing={8}
              align="stretch">
              <Box
                p={8}
                borderRadius="xl"
                border="1px"
                borderColor={
                  colorMode === "dark" ? "brand.primary" : "brand.secondary"
                }
                maxW="md"
                w="full"
                textAlign="left">
                <VStack spacing={6}>
                  <Heading
                    size="xl"
                    fontFamily="Nasalization"
                    color={
                      colorMode === "dark" ? "brand.primary" : "brand.secondary"
                    }>
                    {t("chatbot.pricing.plan.title", "Plano Básico")}
                  </Heading>
                  <HStack spacing={2} justify="center">
                    <Text fontSize="4xl" fontWeight="bold">
                      R$ 59
                    </Text>
                    <Text fontSize="lg" color="gray.500">
                      ,90/mês
                    </Text>
                  </HStack>
                  <VStack spacing={4} align="start" w="full">
                    <HStack>
                      <Icon
                        as={FiCheck}
                        color={
                          colorMode === "dark"
                            ? "brand.primary"
                            : "brand.secondary"
                        }
                      />
                      <Text>Agentes de IA Ilimitados</Text>
                    </HStack>
                    <HStack>
                      <Icon
                        as={FiCheck}
                        color={
                          colorMode === "dark"
                            ? "brand.primary"
                            : "brand.secondary"
                        }
                      />
                      <Text>Base de conhecimento personalizada</Text>
                    </HStack>
                    <HStack>
                      <Icon
                        as={FiCheck}
                        color={
                          colorMode === "dark"
                            ? "brand.primary"
                            : "brand.secondary"
                        }
                      />
                      <Text>Embeds ilimitados</Text>
                    </HStack>
                    <HStack>
                      <Icon
                        as={FiCheck}
                        color={
                          colorMode === "dark"
                            ? "brand.primary"
                            : "brand.secondary"
                        }
                      />
                      <Text>Atendimento 24/7</Text>
                    </HStack>
                    <HStack>
                      <Icon
                        as={FiCheck}
                        color={
                          colorMode === "dark"
                            ? "brand.primary"
                            : "brand.secondary"
                        }
                      />
                      <Text>Escolha vários modelos de IA</Text>
                    </HStack>
                  </VStack>
                  <Button
                    size="lg"
                    bg="brand.primary"
                    color="black"
                    _hover={{
                      bg: "brand.secondary",
                      color: "white",
                    }}
                    w="full"
                    onClick={() => handlePlanSelect("Plano Básico")}
                    fontFamily="Nasalization">
                    {t("chatbot.pricing.cta", "Começar Agora")}
                  </Button>
                </VStack>
              </Box>
              <Box
                p={8}
                borderRadius="xl"
                border="1px"
                borderColor={
                  colorMode === "dark" ? "brand.primary" : "brand.secondary"
                }
                maxW="md"
                w="full"
                textAlign="left">
                <VStack spacing={6}>
                  <Heading
                    size="xl"
                    fontFamily="Nasalization"
                    color={
                      colorMode === "dark" ? "brand.primary" : "brand.secondary"
                    }>
                    {t("chatbot.pricing.plan.title", "Plano Zap")}
                  </Heading>
                  <HStack spacing={2} justify="center">
                    <Text fontSize="4xl" fontWeight="bold">
                      R$ 79
                    </Text>
                    <Text fontSize="lg" color="gray.500">
                      ,90/mês
                    </Text>
                  </HStack>
                  <VStack spacing={4} align="start" w="full">
                    <HStack>
                      <Icon
                        as={FiCheck}
                        color={
                          colorMode === "dark"
                            ? "brand.primary"
                            : "brand.secondary"
                        }
                      />
                      <Text>Recursos do plano básico</Text>
                    </HStack>
                    <HStack>
                      <Icon
                        as={FiCheck}
                        color={
                          colorMode === "dark"
                            ? "brand.primary"
                            : "brand.secondary"
                        }
                      />
                      <Text>Integração WhatsApp</Text>
                    </HStack>
                    <HStack>
                      <Icon
                        as={FiX}
                        color={
                          colorMode === "dark"
                            ? "brand.primary"
                            : "brand.secondary"
                        }
                      />
                      <Text>Mensagens Whatsapp Business</Text>
                    </HStack>
                    <HStack>
                      <Icon
                        as={FiX}
                        color={
                          colorMode === "dark"
                            ? "brand.primary"
                            : "brand.secondary"
                        }
                      />
                      <Text>Número dedicado Whatsapp</Text>
                    </HStack>
                  </VStack>
                  <Button
                    size="lg"
                    bg="brand.primary"
                    color="black"
                    _hover={{
                      bg: "brand.secondary",
                      color: "white",
                    }}
                    w="full"
                    onClick={() => handlePlanSelect("Plano Zap")}
                    fontFamily="Nasalization">
                    {t("chatbot.pricing.cta", "Começar Agora")}
                  </Button>
                </VStack>
              </Box>

              <Box
                p={8}
                borderRadius="xl"
                border="1px"
                borderColor={
                  colorMode === "dark" ? "brand.primary" : "brand.secondary"
                }
                maxW="md"
                w="full"
                textAlign="left">
                <VStack spacing={6}>
                  <Heading
                    size="xl"
                    fontFamily="Nasalization"
                    color={
                      colorMode === "dark" ? "brand.primary" : "brand.secondary"
                    }>
                    {t("chatbot.pricing.plan.title", "Enterprise")}
                  </Heading>
                  <HStack spacing={2} justify="center">
                    <Text fontSize="4xl" fontWeight="bold">
                      R$ --
                    </Text>
                    <Text fontSize="lg" color="gray.500">
                      /mês
                    </Text>
                  </HStack>
                  <VStack spacing={4} align="start" w="full">
                    <HStack>
                      <Icon
                        as={FiCheck}
                        color={
                          colorMode === "dark"
                            ? "brand.primary"
                            : "brand.secondary"
                        }
                      />
                      <Text>Recursos do plano Básico & Zap</Text>
                    </HStack>

                    <HStack>
                      <Icon
                        as={FiCheck}
                        color={
                          colorMode === "dark"
                            ? "brand.primary"
                            : "brand.secondary"
                        }
                      />
                      <Text>Integrações personalizadas</Text>
                    </HStack>
                  </VStack>
                  <Button
                    size="lg"
                    bg="brand.primary"
                    color="black"
                    _hover={{
                      bg: "brand.secondary",
                      color: "white",
                    }}
                    w="full"
                    onClick={() =>
                      (window.location.href = "https://araria.com.br/contact")
                    }
                    fontFamily="Nasalization">
                    {t("chatbot.pricing.cta", "Contato")}
                  </Button>
                </VStack>
              </Box>
            </Stack>
          </VStack>
          <Box p={5}>
            <Text textAlign="left" fontStyle="italic">
              * O consumo de recursos de Inteligência Artificial é cobrado a
              parte no modelo pay-as-you-go (pague apenas o que usar) e será
              deduzido de seu saldo na Araria, o valor depende do modelo de IA
              escolhido (ChatGPT, Claude, Gemini), e também vai variar de acordo
              com volume de mensagens do seu agente.{" "}
              <Text textAlign="left" fontStyle="italic">
                Para saber os preços de cada modelo clique em "Ver Modelos", na{" "}
                <Link fontWeight="bold" href="https://araria.com.br">
                  Página Inicial da Araria
                </Link>
              </Text>
            </Text>
          </Box>
          <Box p={5}>
            <Text textAlign="left" fontStyle="italic">
              * O Plano Zap não inclui custos por envio de mensagens do WhatsApp
              Business, nem o custo mensal relacionado ao número de telefone
              dedicado ao WhatsApp. Nossa integração é feita com o provedor
              Twilio que tem o melhor preço do mercado para os serviços de
              mensagens WhatsApp.
            </Text>
            <Text>
              Para saber os preços de mensagens WhatsApp, use a{" "}
              <Link
                fontWeight="bold"
                href="https://www.twilio.com/pt-br/whatsapp/pricing">
                Calculadora de custos da Twilio
              </Link>
              .
            </Text>
          </Box>
        </Container>
      </Box>

      {/* Final CTA Section */}
      <Box py={20} bg={colorMode === "dark" ? "gray.900" : "white"}>
        <Container maxW="container.lg">
          <VStack spacing={8} textAlign="center">
            <Heading
              size="xl"
              fontFamily="Nasalization"
              bgGradient="linear(to-r, brand.primary, brand.secondary)"
              bgClip="text">
              {t(
                "chatbot.cta.title",
                "Transforme seu Atendimento ao Cliente Hoje"
              )}
            </Heading>
            <Text
              fontSize="lg"
              color={colorMode === "dark" ? "gray.400" : "gray.600"}
              maxW="2xl">
              {t(
                "chatbot.cta.subtitle",
                "Integre agentes de IA inteligentes em seu site e ofereça um atendimento excepcional aos seus clientes"
              )}
            </Text>
            <Button
              size="lg"
              bg="brand.primary"
              color="black"
              _hover={{
                bg: "brand.secondary",
                color: "white",
              }}
              onClick={() => handlePlanSelect("Plano Básico")}
              fontFamily="Nasalization">
              {t("chatbot.cta.button", "Eu Quero!")}
            </Button>
            <iframe width={isMobile ? "350px" : "560px"} height={isMobile ? "200px" : "310px"} src="https://www.youtube.com/embed/_a0PNYsmDN0?si=D5icSg9_b-06286H" title="YouTube video player"  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"  ></iframe>
          </VStack>
        </Container>
      </Box>

      {/* Contact Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent
          bg={useColorModeValue("white", "gray.800")}
          borderColor={useColorModeValue("gray.200", "gray.700")}>
          <ModalHeader fontFamily="Nasalization">
            {t("chatbot.modal.title", "Solicitar")} {selectedPlan}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Box as="form" onSubmit={handleSubmit}>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>{t("common.contact.form.name", "Nome")}</FormLabel>
                  <Input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    bg={useColorModeValue("white", "gray.800")}
                    borderColor={useColorModeValue("gray.200", "gray.700")}
                    _hover={{ borderColor: "brand.primary" }}
                    _focus={{ borderColor: "brand.primary", boxShadow: "none" }}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>
                    {t("common.contact.form.email", "E-mail")}
                  </FormLabel>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    bg={useColorModeValue("white", "gray.800")}
                    borderColor={useColorModeValue("gray.200", "gray.700")}
                    _hover={{ borderColor: "brand.primary" }}
                    _focus={{ borderColor: "brand.primary", boxShadow: "none" }}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>
                    {t("common.contact.form.phone", "Telefone")}
                  </FormLabel>
                  <Input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    bg={useColorModeValue("white", "gray.800")}
                    borderColor={useColorModeValue("gray.200", "gray.700")}
                    _hover={{ borderColor: "brand.primary" }}
                    _focus={{ borderColor: "brand.primary", boxShadow: "none" }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>
                    {t("common.contact.form.site", "Website")}
                  </FormLabel>
                  <Input
                    type="text"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    bg={useColorModeValue("white", "gray.800")}
                    borderColor={useColorModeValue("gray.200", "gray.700")}
                    _hover={{ borderColor: "brand.primary" }}
                    _focus={{ borderColor: "brand.primary", boxShadow: "none" }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>
                    {t("common.contact.form.plan", "Plano")}
                  </FormLabel>
                  <Input type="text" value={selectedPlan || ""} isReadOnly />
                </FormControl>
              </VStack>
            </Box>
          </ModalBody>

          <ModalFooter>
            <Button
              colorScheme="brand"
              mr={3}
              onClick={handleSubmit}
              isLoading={isSubmitting}
              loadingText={t("common.contact.form.submit", "Submit")}
              fontFamily="Nasalization">
              {t("common.contact.form.submit", "Submit")}
            </Button>
            <Button variant="ghost" onClick={onClose}>
              {t("common.cancel", "Cancel")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Footer />
    </Box>
  );
};
